/**
 * The field vocabulary shared by every surface that edits a subcontractor:
 * the option lists, the patterns and the validators.
 *
 * Extracted from `SubcontractorForm` unchanged so the full edit form and the
 * per-section edit modals on the profile page cannot drift on what a valid
 * EIN, CSLB licence number or ACH routing number is. Nothing here is new —
 * moving it is the whole change.
 */
import type { UploadFile } from "antd"

type TFunction = (key: string) => string

// LLC, Corp and S-Corp are US legal forms and stay as they are in every
// language; the other two are ordinary words.
export const entityTypeOptions = (t: TFunction) => [
  { label: "LLC", value: "llc" },
  { label: "Corp", value: "corp" },
  { label: "S-Corp", value: "s_corp" },
  { label: t("subcontractorForm.entitySoleProp"), value: "sole_prop" },
  { label: t("subcontractorForm.entityPartnership"), value: "partnership" }
]

export const EIN_PATTERN = /^\d{2}-?\d{7}$/
export const SSN_PATTERN = /^\d{3}-?\d{2}-?\d{4}$/

export const cslbStatusOptions = (t: TFunction) => [
  { label: t("subcontractorForm.cslbActive"), value: "active" },
  { label: t("subcontractorForm.cslbSuspended"), value: "suspended" },
  { label: t("subcontractorForm.cslbExpired"), value: "expired" },
  { label: t("subcontractorForm.cslbInactive"), value: "inactive" }
]

// The CSLB's rule is numeric, up to 8 digits — real licenses run 6-7 digits.
export const CSLB_LICENSE_NUMBER_PATTERN = /^\d{1,8}$/
export const CSLB_MINIMUM_BOND_AMOUNT = 25000

// A plain `type`/`min`/`transform` rule runs `transform` even on an untouched
// (undefined) field, turning it into 0/NaN and tripping `min` on a field nobody
// filled in — a custom validator short-circuits first. Shared by the contractor's
// bond and the qualifying individual's, which have the same $25,000 minimum.
export const validateBondAmount = (_rule: unknown, value: unknown) => {
  if (value === undefined || value === null || value === "") {
    return Promise.resolve()
  }
  if (Number(value) < CSLB_MINIMUM_BOND_AMOUNT) {
    return Promise.reject(
      new Error(
        `Minimum bond amount is $${CSLB_MINIMUM_BOND_AMOUNT.toLocaleString()}`
      )
    )
  }
  return Promise.resolve()
}

export const TAX_ID_TYPE_OPTIONS = [
  { label: "EIN", value: "ein" },
  { label: "SSN", value: "ssn" }
]

// ACH is a US banking network, not a word to translate.
export const paymentMethodOptions = (t: TFunction) => [
  { label: "ACH", value: "ach" },
  { label: t("subcontractorForm.paymentWire"), value: "wire" },
  { label: t("subcontractorForm.paymentCheck"), value: "check" }
]
export const ACH_ROUTING_NUMBER_PATTERN = /^\d{9}$/
export const ACH_ACCOUNT_NUMBER_PATTERN = /^\d{4,17}$/

export const normFile = (e: { fileList?: UploadFile[] } | UploadFile[]) =>
  Array.isArray(e) ? e : e?.fileList
